import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, API, downloadUrl, authQuery } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import LodgeDatePicker from "../components/LodgeDatePicker";
import { ObedienceContributionTab, ContributionsOverviewTab } from "../components/ObedienceContribution";
import { fetchObedienceType, pluralKey } from "../lib/gender";
import { SORTED_CURRENCIES } from "../lib/currencies";
import {
  Coins, CheckCircle2, Clock, Settings, FileDown, CreditCard,
  Undo2, Trash2, Plus, X, AlertCircle, RefreshCw, FileSpreadsheet, Files, Pencil, Send, UserPlus
} from "lucide-react";

const METHODS_KEY = ["Numerar", "Transfer bancar", "Card", "Altul"];
// Map DB-stored Romanian method value → i18n key so the UI renders the
// label in the active language.
const METHOD_TKEY = {
  "Numerar": "dues.method_numerar",
  "Transfer bancar": "dues.method_transfer_bancar",
  "Card": "dues.method_card",
  "Altul": "dues.method_altul",
};

function currentPeriodLabel(type) {
  const d = new Date();
  if (type === "annual") return String(d.getFullYear());
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${mo}`;
}

function formatMoney(v, ccy) {
  return `${Number(v || 0).toFixed(2)} ${ccy || "RON"}`;
}

export default function DuesPage() {
  const { t, i18n } = useTranslation();
  const [obedienceType, setObedienceType] = useState("regulara");
  useEffect(() => { fetchObedienceType().then(setObedienceType); }, []);
  const tp = (base, opts) => t(pluralKey(base, obedienceType), { ...opts, defaultValue: t(base, opts) });
  const locale =t("dues.en_gb");
  const formatDate = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleDateString(locale); } catch { return v; }
  };
  const periodHuman = (label, type) => {
    if (type === "annual") return `${t("dues.year")} ${label}`;
    try {
      const [y, m] = label.split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
    } catch { return label; }
  };
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();
  const [tab, setTab] = useState("dues");
  const showContribTab = isAdmin;

  const [periodType, setPeriodType] = useState("monthly");
  const [period, setPeriod] = useState(currentPeriodLabel("monthly"));
  const [yearTotal, setYearTotal] = useState(null);
  const [dues, setDues] = useState([]);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({
    mode: "single",                       // "single" | "range" — toggles bulk retroactive generation
    period_type: "monthly",
    period_label: currentPeriodLabel("monthly"),
    start_month: currentPeriodLabel("monthly"),
    end_month: currentPeriodLabel("monthly"),
    amount: "",
  });
  const [showIndividual, setShowIndividual] = useState(false);
  const [indForm, setIndForm] = useState({
    member_id: "",
    amount: "",
    category: "regular",
    grade: "",
    period_type: "monthly",
    period_label: currentPeriodLabel("monthly"),
    period_start_date: "",
    notes: "",
    notify_mobile: true,
  });
  const [indBusy, setIndBusy] = useState(false);
  const [sendingMobile, setSendingMobile] = useState({});
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ paid_at: "", method: "Numerar", notes: "" });

  // ── Iter21 unified emit-notice modal ────────────────────────────────
  // Replaces the dual "Generate" + "Individual notice" entry points with
  // one flow that handles single OR multi member, every category, exempt
  // workflow, and "achitat deja" inline-payment.
  const [showEmit, setShowEmit] = useState(false);
  const [emitForm, setEmitForm] = useState(null);

  // Bulk-pay selection state (checkbox column on the dues table).
  const [bulkSelected, setBulkSelected] = useState({});  // {duesId: bool}
  const [bulkBusy, setBulkBusy] = useState(false);

  // Treasury report modal
  const [showReport, setShowReport] = useState(false);
  const [reportForm, setReportForm] = useState({ period_type: "annual", year: String(new Date().getFullYear()), quarter: "Q1" });

  // Categories shown in the unified emit modal.
  const CATEGORIES = useMemo(() => ([
    { key: "cotizatie",     labelRo: "Cotizație",          labelEn: "Dues" },
    { key: "taxa_initiere", labelRo: "Taxă inițiere",      labelEn: "Initiation fee" },
    { key: "taxa_crestere", labelRo: "Taxă creștere grad", labelEn: "Grade promotion fee" },
    { key: "taxa_afiliere", labelRo: "Taxă afiliere",      labelEn: "Affiliation fee" },
    { key: "donatie",       labelRo: "Donație",            labelEn: "Donation" },
    { key: "alta_taxa",     labelRo: "Altă taxă",          labelEn: "Other fee" },
  ]), []);

  const newEmitForm = () => ({
    member_ids: [],          // [] = nobody, or 1+ ids — UI exposes single+multi+all toggles
    audience: "single",      // single | multi | all
    category: "cotizatie",
    amount: "",
    currency: settings?.currency || "RON",
    notes: "",
    period_type: "monthly",
    range_mode: false,        // when true → start_month/end_month
    period_label: currentPeriodLabel("monthly"),
    start_month: currentPeriodLabel("monthly"),
    end_month: currentPeriodLabel("monthly"),
    mark_paid: false,
    paid_amount: "",
    payment_method: "Numerar",
    is_exempt: false,
    exempt_reason: "",
    exempt_until: "",
  });

  const openEmit = () => {
    setEmitForm(newEmitForm());
    setShowEmit(true);
  };

  const submitEmit = async (e) => {
    e.preventDefault();
    if (!emitForm) return;
    if (emitForm.audience === "all") {
      // Resolve "all" → every active member id.
      emitForm.member_ids = members.filter((m) => m.status === "Active" && m.is_deleted !== true).map((m) => m.id);
    }
    if (!emitForm.member_ids.length) {
      toast.error(t("dues.pick_at_least_one_member"));
      return;
    }
    if (emitForm.is_exempt && !(emitForm.exempt_reason || "").trim()) {
      toast.error(t("dues.exemption_reason_is_required"));
      return;
    }
    // For exempt invoices the amount is forced to 0 by the backend (the
    // brother owes nothing). Skip the positive-amount check in that case.
    if (!emitForm.is_exempt && (!emitForm.amount || Number(emitForm.amount) <= 0)) {
      toast.error(t("dues.enter_a_positive_amount"));
      return;
    }
    const payload = {
      member_ids: emitForm.member_ids,
      category: emitForm.category,
      amount: emitForm.is_exempt ? 0 : Number(emitForm.amount),
      currency: emitForm.currency || null,
      notes: emitForm.notes || null,
      mark_paid: !!emitForm.mark_paid,
      paid_amount: emitForm.mark_paid && emitForm.paid_amount ? Number(emitForm.paid_amount) : null,
      payment_method: emitForm.mark_paid ? emitForm.payment_method : null,
      is_exempt: !!emitForm.is_exempt,
      exempt_reason: emitForm.is_exempt ? emitForm.exempt_reason : null,
      exempt_until: emitForm.is_exempt && emitForm.exempt_until ? emitForm.exempt_until : null,
    };
    if (emitForm.category === "cotizatie") {
      payload.period_type = emitForm.period_type;
      if (emitForm.audience === "single" && emitForm.range_mode) {
        payload.start_month = emitForm.start_month;
        payload.end_month = emitForm.end_month;
      } else {
        payload.period_label = emitForm.period_label;
      }
    }
    try {
      const { data } = await api.post("/dues/emit-bulk", payload);
      toast.success(t("dues.bulk_emit_summary", { created: data.created, skipped: data.skipped, members: data.members, periods: data.periods }));
      setShowEmit(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkMarkPaid = async () => {
    const ids = Object.keys(bulkSelected).filter((k) => bulkSelected[k]);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/dues/bulk-mark-paid", { dues_ids: ids, method: "Numerar" });
      toast.success(t("dues.marked_paid_count", { count: data.updated }));
      setBulkSelected({});
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadReport = async () => {
    const periodVal = reportForm.period_type === "annual"
      ? reportForm.year
      : `${reportForm.year}-${reportForm.quarter}`;
    try {
      await downloadUrl(
        `${API}/dues/report.pdf?period_type=${reportForm.period_type}&period=${encodeURIComponent(periodVal)}&${authQuery().slice(1)}`
      );
      setShowReport(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [d, m, s, p, yt] = await Promise.all([
        api.get(`/dues?period=${encodeURIComponent(period)}`),
        api.get("/members").catch(() => ({ data: [] })),
        api.get("/dues/settings"),
        api.get("/dues/periods"),
        api.get("/dues/year-total"),
      ]);
      setDues(d.data);
      setMembers(m.data);
      setSettings(s.data);
      setSettingsForm(s.data);
      setPeriods(p.data);
      setYearTotal(yt.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]);

  const memberById = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  const summary = useMemo(() => {
    const total = dues.reduce((s, d) => s + (d.amount || 0), 0);
    const paid = dues.filter((d) => d.status === "paid").reduce((s, d) => s + (d.amount || 0), 0);
    const pendingCount = dues.filter((d) => d.status === "pending").length;
    const overdueCount = dues.filter((d) => d.status === "pending" && d.due_date && d.due_date < new Date().toISOString().slice(0, 10)).length;
    return {
      total,
      paid,
      pending: total - paid,
      count: dues.length,
      paidCount: dues.length - pendingCount,
      pendingCount,
      overdueCount,
    };
  }, [dues]);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/dues/settings", {
        monthly_amount: Number(settingsForm.monthly_amount),
        annual_amount: Number(settingsForm.annual_amount),
        currency: settingsForm.currency || "RON",
        due_days: Number(settingsForm.due_days) || 15,
        billing_frequency: settingsForm.billing_frequency === "annual" ? "annual" : "monthly",
        auto_emit: !!settingsForm.auto_emit,
        bank_beneficiary: (settingsForm.bank_beneficiary || "").trim() || null,
        bank_iban: (settingsForm.bank_iban || "").trim() || null,
        bank_name: (settingsForm.bank_name || "").trim() || null,
      });
      toast.success(t("dues.settingsSaved"));
      setShowSettings(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const loadConnect = async () => {
    try {
      const { data } = await api.get("/payments/connect/status");
      setConnect(data);
    } catch { /* non-admin or transient */ }
  };

  useEffect(() => {
    loadConnect();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return") {
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(async () => {
        try {
          const { data } = await api.get("/payments/connect/status");
          setConnect(data);
          if (data.charges_enabled) toast.success(t("dues.connectDone"));
          else toast.info(t("dues.connectIncomplete"));
          setShowSettings(true);
        } catch { /* noop */ }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConnect = async () => {
    setConnectBusy(true);
    try {
      const { data } = await api.post("/payments/connect/onboard", {
        origin_url: window.location.origin,
      });
      if (data?.onboarding_url) window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setConnectBusy(false);
    }
  };

  const openGenerate = () => {
    setGenForm({
      mode: "single",
      period_type: periodType,
      period_label: period,
      start_month: currentPeriodLabel("monthly"),
      end_month: currentPeriodLabel("monthly"),
      amount: "",
    });
    setShowGenerate(true);
  };

  const generate = async (e) => {
    e.preventDefault();
    try {
      if (genForm.mode === "range") {
        if (!genForm.start_month || !genForm.end_month) {
          toast.error(t("dues.rangeBothRequired", "Selectează luna de început și luna de sfârșit."));
          return;
        }
        if (genForm.start_month > genForm.end_month) {
          toast.error(t("dues.rangeOrder", "Luna de început trebuie să fie ≤ luna de sfârșit."));
          return;
        }
        const payload = { start_month: genForm.start_month, end_month: genForm.end_month };
        if (genForm.amount) payload.amount = Number(genForm.amount);
        const { data } = await api.post("/dues/generate-range", payload);
        toast.success(
          t("dues.rangeGeneratedSuccess",
            "Generate {{created}} cotizații pentru {{count}} luni (sărite: {{skipped}}).",
            { created: data.created, skipped: data.skipped, count: data.months?.length || 0 },
          ),
        );
        setPeriod(genForm.start_month);
        setPeriodType("monthly");
      } else {
        const payload = { period_type: genForm.period_type, period_label: genForm.period_label };
        if (genForm.amount) payload.amount = Number(genForm.amount);
        const { data } = await api.post("/dues/generate", payload);
        toast.success(t("dues.generatedSuccess", { created: data.created, due: data.due_date, skipped: data.skipped }));
        setPeriod(genForm.period_label);
        setPeriodType(genForm.period_type);
      }
      setShowGenerate(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openIndividual = () => {
    setIndForm({
      member_id: "",
      amount: settings?.monthly_amount ? String(settings.monthly_amount) : "",
      category: "regular",
      grade: "",
      period_type: "monthly",
      period_label: currentPeriodLabel("monthly"),
      period_start_date: "",
      notes: "",
      notify_mobile: true,
    });
    setShowIndividual(true);
  };

  const submitIndividual = async (e) => {
    e.preventDefault();
    if (!indForm.member_id) { toast.error(t("dues.pickMember")); return; }
    if (!indForm.amount || Number(indForm.amount) <= 0) { toast.error(t("dues.amountRequired")); return; }
    if (indForm.category === "grade_promotion" && !indForm.grade) { toast.error(t("dues.gradeRequired")); return; }
    setIndBusy(true);
    try {
      const payload = {
        member_id: indForm.member_id,
        amount: Number(indForm.amount),
        category: indForm.category,
        period_type: indForm.period_type,
        period_label: indForm.period_label || undefined,
        notify_mobile: !!indForm.notify_mobile,
        notes: indForm.notes || undefined,
      };
      if (indForm.category === "grade_promotion") payload.grade = Number(indForm.grade);
      if (indForm.period_type === "annual" && indForm.period_start_date) payload.period_start_date = indForm.period_start_date;
      await api.post("/dues/individual", payload);
      toast.success(t("dues.individualSuccess"));
      setShowIndividual(false);
      setPeriod(payload.period_label);
      setPeriodType(payload.period_type);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setIndBusy(false); }
  };

  const sendToMobile = async (row) => {
    setSendingMobile((prev) => ({ ...prev, [row.id]: true }));
    try {
      await api.post(`/dues/${row.id}/send-to-mobile`);
      toast.success(t("dues.sentToMobile", { name: row.member_name }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSendingMobile((prev) => ({ ...prev, [row.id]: false })); }
  };

  const openPay = (d) => {
    setPayFor(d);
    setPayForm({ paid_at: new Date().toISOString().slice(0, 10), method: "Numerar", notes: "" });
  };

  const submitPay = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/dues/${payFor.id}/mark-paid`, payForm);
      toast.success(t("dues.paymentConfirmed", { name: payFor.member_name }));
      setPayFor(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unmarkPaid = async (d) => {
    const ok = await confirm({
      title: t("dues.unpaidConfirmTitle"),
      message: t("dues.unpaidConfirmMsg", { name: d.member_name }),
      confirmText: t("dues.unpaidConfirmBtn"),
    });
    if (!ok) return;
    try { await api.post(`/dues/${d.id}/mark-unpaid`); toast.success(t("dues.unpaidDone")); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const removeDues = async (d) => {
    const ok = await confirm({
      title: t("dues.deleteConfirmTitle"),
      message: t("dues.deleteConfirmMsg", { name: d.member_name, period: periodHuman(d.period_label, d.period_type) }),
    });
    if (!ok) return;
    try { await api.delete(`/dues/${d.id}`); toast.success(t("dues.deletedDone")); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resetDues = async (d) => {
    const ok = await confirm({
      title:t("dues.reset_invoice"),
      message: t("dues.reset_invoice_confirm", { name: d.member_name, period: d.period_label }),
    });
    if (!ok) return;
    try {
      await api.post(`/dues/${d.id}/reset`);
      toast.success(t("dues.invoice_reset"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [editing, setEditing] = useState(null); // dues row or null
  const [editForm, setEditForm] = useState({ amount: "", period_label: "", due_date: "", notes: "" });
  const openEdit = (d) => {
    setEditing(d);
    setEditForm({
      amount: d.amount,
      period_label: d.period_label,
      due_date: d.due_date,
      notes: d.notes || "",
    });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/dues/${editing.id}`, {
        amount: parseFloat(editForm.amount),
        period_label: editForm.period_label,
        due_date: editForm.due_date,
        notes: editForm.notes,
      });
      toast.success(t("dues.invoice_updated"));
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadNotice = (d) => {
    // authQuery() already encodes ?lang=…
    downloadUrl(`${API}/dues/${d.id}/notice/pdf${authQuery()}`);
  };

  const downloadBulkPdf = (onlyPending = true) => {
    const statusParam = onlyPending ? "&status=pending" : "&status=all";
    downloadUrl(`${API}/dues/bulk/pdf?period=${encodeURIComponent(period)}${statusParam}&${authQuery().slice(1)}`);
  };

  const downloadCsv = () => {
    downloadUrl(`${API}/dues/export/csv?period=${encodeURIComponent(period)}&${authQuery().slice(1)}`);
  };

  const syncWithMembers = async () => {
    try {
      const { data } = await api.post("/dues/sync", { period_type: periodType, period_label: period });
      if (data.created === 0) {
        toast.success(t("dues.syncedAlready"));
      } else {
        toast.success(t("dues.syncedCreated", { count: data.created }));
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="dues-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t("dues.title")}</h1>
          <p className="page-subtitle">{t("dues.subtitle")}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button className="btn-outline-gold flex items-center gap-2" onClick={() => downloadCsv()} data-testid="export-csv-btn" title={t("dues.csvTitle")}>
              <FileSpreadsheet size={14} /> {t("dues.csv")}
            </button>
            <button className="btn-outline-gold flex items-center gap-2" onClick={() => downloadBulkPdf(true)} data-testid="export-bulk-pdf-btn" title={t("dues.bulkPdfTitle")}>
              <Files size={14} /> {t("dues.bulkPdf")}
            </button>
            <button className="btn-outline-gold flex items-center gap-2" onClick={() => setShowSettings(true)} data-testid="dues-settings-btn">
              <Settings size={14} /> {t("dues.settings")}
            </button>
            <button className="btn-outline-gold flex items-center gap-2" onClick={syncWithMembers} data-testid="sync-dues-btn" title={t("dues.syncTitle")}>
              <RefreshCw size={14} /> {t("dues.sync")}
            </button>
            <button className="btn-outline-gold flex items-center gap-2" onClick={() => setShowReport(true)} data-testid="dues-report-btn">
              <FileDown size={14} /> {t("dues.report")}
            </button>
            <button className="btn-primary-gold flex items-center gap-2" onClick={openEmit} data-testid="emit-notice-btn">
              <Plus size={14} /> {t("dues.emitNotice", "EMITE NOTIFICARE")}
            </button>
          </div>
        )}
      </div>

      {showContribTab && (
        <div className="flex gap-2 mt-5 flex-wrap" data-testid="dues-tabs">
          <button
            type="button"
            className={tab === "dues" ? "btn-primary-gold" : "btn-outline-gold"}
            onClick={() => setTab("dues")}
            data-testid="tab-dues"
          >
            {t("contrib.tabDues", "Cotizații")}
          </button>
          <button
            type="button"
            className={tab === "contrib" ? "btn-primary-gold" : "btn-outline-gold"}
            onClick={() => setTab("contrib")}
            data-testid="tab-contrib"
          >
            {user?.child_lodge_id
              ? t("contrib.tabContrib", "Contribuție Obediență")
              : t("contrib.tabOverview", "Contribuții Loji")}
          </button>
        </div>
      )}

      {tab === "contrib" && (user?.child_lodge_id ? <ObedienceContributionTab /> : <ContributionsOverviewTab />)}

      <div style={tab === "contrib" ? { display: "none" } : undefined}>
      <div className="gold-divider my-8" />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="tile" data-testid="dues-year-collected-tile">
          <div className="flex items-start justify-between">
            <div>
              <div className="tile-label">{t("dues.yearCollected", { year: yearTotal?.year || new Date().getFullYear() })}</div>
              <div className="tile-value" style={{ fontSize: "1.6rem" }}>{formatMoney(yearTotal?.total || 0, settings?.currency)}</div>
              <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>{t("dues.yearCollectedCount", { count: yearTotal?.count || 0 })}</div>
            </div>
            <Coins size={26} strokeWidth={1.3} color="#c9a84c" />
          </div>
        </div>
        <div className="tile" style={{ borderTop: "3px solid #3e6a3c" }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="tile-label">{t("dues.paid")}</div>
              <div className="tile-value" style={{ fontSize: "1.6rem", color: "#3e6a3c" }}>{formatMoney(summary.paid, settings?.currency)}</div>
              <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>{t("dues.paidFrom", { paid: summary.paidCount, total: summary.count })}</div>
            </div>
            <CheckCircle2 size={26} strokeWidth={1.3} color="#3e6a3c" />
          </div>
        </div>
        <div className="tile" style={{ borderTop: "3px solid #9a7530" }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="tile-label">{t("dues.pending")}</div>
              <div className="tile-value" style={{ fontSize: "1.6rem", color: "#9a7530" }}>{formatMoney(summary.pending, settings?.currency)}</div>
              <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>{t("dues.pendingCount", { count: summary.pendingCount })}</div>
            </div>
            <Clock size={26} strokeWidth={1.3} color="#9a7530" />
          </div>
        </div>
        <div className="tile" style={{ borderTop: "3px solid #8a2a2a" }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="tile-label">{t("dues.overdue")}</div>
              <div className="tile-value" style={{ fontSize: "1.6rem", color: "#8a2a2a" }}>{summary.overdueCount}</div>
              <div className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.25rem" }}>{t("dues.overdueLabel")}</div>
            </div>
            <AlertCircle size={26} strokeWidth={1.3} color="#8a2a2a" />
          </div>
        </div>
      </div>

      {/* Period selector */}
      <div className="panel mt-8">
        <div className="panel-head flex items-center gap-3" style={{ justifyContent: "space-between" }}>
          <span>{t("dues.selectedPeriod")}</span>
          <span style={{ fontSize: "0.7rem", color: "#e8c97a" }}>{periodHuman(period, periodType)}</span>
        </div>
        <div className="panel-body">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              <button className={`filter-chip ${periodType === "monthly" ? "active" : ""}`} onClick={() => { setPeriodType("monthly"); setPeriod(currentPeriodLabel("monthly")); }}>{t("dues.monthly")}</button>
              <button className={`filter-chip ${periodType === "annual" ? "active" : ""}`} onClick={() => { setPeriodType("annual"); setPeriod(currentPeriodLabel("annual")); }}>{t("dues.annual")}</button>
            </div>
            {periodType === "monthly" ? (
              <input
                type="month"
                className="input-lodge"
                style={{ maxWidth: 200 }}
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                data-testid="period-picker-month"
              />
            ) : (
              <input
                type="number"
                min="2000"
                max="2100"
                className="input-lodge"
                style={{ maxWidth: 140 }}
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                data-testid="period-picker-year"
              />
            )}
            <div className="flex-1" />
            {periods.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="kv-label" style={{ alignSelf: "center" }}>{t("dues.previousPeriods")}</span>
                {periods.slice(0, 6).map((p) => (
                  <button
                    key={`${p.period_type}-${p.period_label}`}
                    className={`filter-chip ${period === p.period_label ? "active" : ""}`}
                    onClick={() => { setPeriodType(p.period_type); setPeriod(p.period_label); }}
                  >
                    {periodHuman(p.period_label, p.period_type)} ({p.paid}/{p.count})
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dues table */}
      <div className="mt-8 table-wrap">
        <table className="lodge-table" data-testid="dues-table">
          <thead>
            <tr>
              <th>{t("dues.member")}</th>
              <th data-mobile-hide="true">{t("dues.grade")}</th>
              <th>{t("dues.amount")}</th>
              <th data-narrow-hide="true">{t("dues.dueDate")}</th>
              <th>{t("dues.status")}</th>
              <th data-narrow-hide="true">{t("dues.payment")}</th>
              <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: "2rem" }}><div className="spinner" /></td></tr>
            ) : dues.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>
                  {t("dues.noInvoices", { period: periodHuman(period, periodType) })} {isAdmin && t("dues.useGenerate")}
                </td>
              </tr>
            ) : (
              dues.map((d) => {
                const overdue = d.status === "pending" && d.due_date && d.due_date < today;
                const m = memberById[d.member_id];
                return (
                  <tr key={d.id} data-testid={`dues-row-${d.id}`}>
                    <td>
                      <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>{d.member_name}</div>
                      {m?.email && <div style={{ fontSize: "0.78rem", color: "#858481", fontStyle: "italic" }}>{m.email}</div>}
                    </td>
                    <td data-mobile-hide="true">{m ? <span className="badge-grade">{m.grade}</span> : "—"}</td>
                    <td style={{ fontFamily: "Cinzel, serif", fontSize: "0.92rem" }}>{formatMoney(d.amount, d.currency)}</td>
                    <td data-narrow-hide="true">
                      <span style={{ color: overdue ? "#8a2a2a" : "#1a1a19" }}>{formatDate(d.due_date)}</span>
                      {overdue && <span className="badge-status suspended" style={{ marginLeft: 6 }}>{t("dues.overdueBadge")}</span>}
                    </td>
                    <td>
                      {d.status === "paid" ? (
                        <span className="badge-status active"><CheckCircle2 size={11} style={{ display: "inline", marginRight: 3 }} /> {t("dues.statusPaid")}</span>
                      ) : d.transfer_claimed_at ? (
                        <span className="badge-status" style={{ background: "#e8f0fb", color: "#1d4e89", border: "1px solid #1d4e89" }} title={t("dues.inReviewHint")} data-testid={`dues-inreview-${d.id}`}>{t("dues.statusInReview")}</span>
                      ) : (
                        <span className="badge-status" style={{ background: "#fdf4e0", color: "#9a7530", border: "1px solid #c9a84c" }}>{t("dues.statusPending")}</span>
                      )}
                    </td>
                    <td data-narrow-hide="true" style={{ fontSize: "0.88rem" }}>
                      {d.status === "paid" ? (
                        <>
                          <div>{formatDate(d.paid_at)}</div>
                          {d.method && <div style={{ color: "#858481", fontStyle: "italic", fontSize: "0.78rem" }}>{METHOD_TKEY[d.method] ? t(METHOD_TKEY[d.method]) : d.method}</div>}
                        </>
                      ) : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
                        <button className="btn-outline-gold flex items-center gap-1" onClick={() => downloadNotice(d)} data-testid={`notice-${d.id}`}>
                          <FileDown size={12} /> {t("dues.invoice")}
                        </button>
                        {isAdmin && (
                          <button
                            className="btn-outline-gold flex items-center gap-1"
                            onClick={() => sendToMobile(d)}
                            disabled={!!sendingMobile[d.id]}
                            title={t("dues.sendToMobileTitle")}
                            data-testid={`send-mobile-${d.id}`}
                          >
                            <Send size={12} /> {sendingMobile[d.id] ? "..." : t("dues.sendToMobile")}
                          </button>
                        )}
                        {isAdmin && (
                          d.status === "paid" ? (
                            <button className="btn-outline-gold flex items-center gap-1" onClick={() => unmarkPaid(d)} title={t("dues.unmarkPaid")}>
                              <Undo2 size={12} />
                            </button>
                          ) : (
                            <button className="btn-primary-gold flex items-center gap-1" onClick={() => openPay(d)} data-testid={`pay-${d.id}`}>
                              <CheckCircle2 size={12} /> {t("dues.markPaid")}
                            </button>
                          )
                        )}
                        {isAdmin && !user?.child_lodge_id && (
                          <>
                            <button
                              className="btn-outline-gold flex items-center gap-1"
                              onClick={() => openEdit(d)}
                              title={t("dues.edit_invoice_amount_period_etc")}
                              data-testid={`edit-dues-${d.id}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              className="btn-outline-gold flex items-center gap-1"
                              onClick={() => resetDues(d)}
                              title={t("dues.reset_to_pending_clear_payment")}
                              data-testid={`reset-dues-${d.id}`}
                            >
                              <RefreshCw size={12} />
                            </button>
                          </>
                        )}
                        {isAdmin && (
                          <button className="btn-danger" onClick={() => removeDues(d)} title={t("dues.deleteInvoice")}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Settings modal */}
      {showSettings && settingsForm && (
        <div className="dialog-overlay" onClick={() => setShowSettings(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={saveSettings} data-testid="dues-settings-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("dues.settingsTitle")}</h2>
              <button type="button" onClick={() => setShowSettings(false)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-lodge">{t("dues.monthlyAmount")}</label>
                <input type="number" step="0.01" min="0" className="input-lodge" required value={settingsForm.monthly_amount} onChange={(e) => setSettingsForm({ ...settingsForm, monthly_amount: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("dues.annualAmount")}</label>
                <input type="number" step="0.01" min="0" className="input-lodge" required value={settingsForm.annual_amount} onChange={(e) => setSettingsForm({ ...settingsForm, annual_amount: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("dues.currency")}</label>
                <select className="input-lodge" value={settingsForm.currency} onChange={(e) => setSettingsForm({ ...settingsForm, currency: e.target.value })}>
                  {SORTED_CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("dues.dueDays")}</label>
                <input type="number" min="1" max="365" className="input-lodge" value={settingsForm.due_days} onChange={(e) => setSettingsForm({ ...settingsForm, due_days: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("dues.billingFrequency")}</label>
                <select className="input-lodge" value={settingsForm.billing_frequency || "monthly"} onChange={(e) => setSettingsForm({ ...settingsForm, billing_frequency: e.target.value })} data-testid="dues-billing-frequency">
                  <option value="monthly">{t("dues.typeMonthly")}</option>
                  <option value="annual">{t("dues.typeAnnual")}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!settingsForm.auto_emit}
                    onChange={(e) => setSettingsForm({ ...settingsForm, auto_emit: e.target.checked })}
                    data-testid="dues-auto-emit"
                  />
                  <span className="label-lodge" style={{ margin: 0 }}>{t("dues.autoEmit")}</span>
                </label>
                <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 4 }}>
                  {t("dues.autoEmitHint")}
                </p>
              </div>
              <div className="md:col-span-2" style={{ borderTop: "1px solid #e5dcc8", paddingTop: 14, marginTop: 6 }} data-testid="dues-payment-settings">
                <h3 style={{ fontWeight: 700, color: "#0d1b2a", marginBottom: 4 }}>{t("dues.payChannelsTitle")}</h3>
                <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 10 }}>{t("dues.payChannelsHint")}</p>
                {/* Stripe Connect */}
                <div className="flex flex-wrap items-center gap-3" style={{ padding: "0.6rem 0.75rem", borderRadius: 8, background: connect?.charges_enabled ? "#eaf4e8" : "rgba(0,0,0,0.03)", marginBottom: 12 }}>
                  <CreditCard size={16} />
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>Stripe</div>
                    <div style={{ fontSize: "0.74rem" }} data-testid="connect-status-label">
                      {connect?.charges_enabled
                        ? <span style={{ color: "#2c5e21", fontWeight: 700 }}>{t("dues.connectActive")}</span>
                        : connect?.connected
                          ? <span style={{ color: "#9a7530", fontWeight: 700 }}>{t("dues.connectPending")}</span>
                          : <span style={{ opacity: 0.6 }}>{t("dues.connectNone")}</span>}
                    </div>
                  </div>
                  <button type="button" className={connect?.charges_enabled ? "btn-outline-gold" : "btn-primary-gold"} disabled={connectBusy} onClick={startConnect} data-testid="connect-stripe-btn">
                    {connectBusy ? "…" : connect?.charges_enabled ? t("dues.connectManage") : connect?.connected ? t("dues.connectContinue") : t("dues.connectStripe")}
                  </button>
                </div>
                {/* Bank transfer details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-lodge">{t("dues.bankBeneficiary")}</label>
                    <input className="input-lodge" value={settingsForm.bank_beneficiary || ""} onChange={(e) => setSettingsForm({ ...settingsForm, bank_beneficiary: e.target.value })} data-testid="bank-beneficiary-input" />
                  </div>
                  <div>
                    <label className="label-lodge">IBAN</label>
                    <input className="input-lodge" value={settingsForm.bank_iban || ""} onChange={(e) => setSettingsForm({ ...settingsForm, bank_iban: e.target.value })} placeholder="RO49 AAAA 1B31 0075 9384 0000" data-testid="bank-iban-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-lodge">{t("dues.bankName")}</label>
                    <input className="input-lodge" value={settingsForm.bank_name || ""} onChange={(e) => setSettingsForm({ ...settingsForm, bank_name: e.target.value })} data-testid="bank-name-input" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowSettings(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold">{t("common.save")}</button>
            </div>
          </form>
        </div>
      )}

      {/* Generate modal */}
      {showGenerate && (
        <div className="dialog-overlay" onClick={() => setShowGenerate(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={generate} data-testid="generate-dues-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("dues.generateTitle")}</h2>
              <button type="button" onClick={() => setShowGenerate(false)}><X size={20} /></button>
            </div>
            <p style={{ color: "#34322d", marginBottom: "1rem" }} dangerouslySetInnerHTML={{ __html: t("dues.generate_intro_html") }} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-lodge">
                  {t("dues.mode")}
                </label>
                <div className="flex gap-2 flex-wrap" data-testid="generate-mode-toggle">
                  <button
                    type="button"
                    className={genForm.mode === "single" ? "btn-primary-gold" : "btn-outline-gold"}
                    onClick={() => setGenForm({ ...genForm, mode: "single" })}
                    data-testid="gen-mode-single"
                  >
                    {t("dues.single_period")}
                  </button>
                  <button
                    type="button"
                    className={genForm.mode === "range" ? "btn-primary-gold" : "btn-outline-gold"}
                    onClick={() => setGenForm({ ...genForm, mode: "range", period_type: "monthly" })}
                    data-testid="gen-mode-range"
                  >
                    {t("dues.range_de_la_p_n")}
                  </button>
                </div>
              </div>

              {genForm.mode === "single" ? (
                <>
                  <div>
                    <label className="label-lodge">{t("dues.typeCotiz")}</label>
                    <select className="input-lodge" value={genForm.period_type} onChange={(e) => setGenForm({ ...genForm, period_type: e.target.value, period_label: currentPeriodLabel(e.target.value) })}>
                      <option value="monthly">{t("dues.typeMonthly")}</option>
                      <option value="annual">{t("dues.typeAnnual")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-lodge">{genForm.period_type === "monthly" ? t("dues.month") : t("dues.year")}</label>
                    {genForm.period_type === "monthly" ? (
                      <input type="month" className="input-lodge" required value={genForm.period_label} onChange={(e) => setGenForm({ ...genForm, period_label: e.target.value })} />
                    ) : (
                      <input type="number" min="2000" max="2100" className="input-lodge" required value={genForm.period_label} onChange={(e) => setGenForm({ ...genForm, period_label: e.target.value })} />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="label-lodge">
                      {t("dues.from_month")}
                    </label>
                    <input
                      type="month"
                      className="input-lodge"
                      required
                      value={genForm.start_month}
                      onChange={(e) => setGenForm({ ...genForm, start_month: e.target.value })}
                      data-testid="gen-range-start"
                    />
                  </div>
                  <div>
                    <label className="label-lodge">
                      {t("dues.to_month")}
                    </label>
                    <input
                      type="month"
                      className="input-lodge"
                      required
                      value={genForm.end_month}
                      onChange={(e) => setGenForm({ ...genForm, end_month: e.target.value })}
                      data-testid="gen-range-end"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="label-lodge">
                  {genForm.mode === "range"
                    ? t("dues.amount_per_month_optional")
                    : t("dues.customAmount")}
                </label>
                <input type="number" step="0.01" min="0" className="input-lodge" placeholder={settings ? t("dues.defaultAmount", { amount: genForm.period_type === "monthly" ? settings.monthly_amount : settings.annual_amount, currency: settings.currency }) : ""} value={genForm.amount} onChange={(e) => setGenForm({ ...genForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowGenerate(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" data-testid="confirm-generate-btn">{t("dues.generateBtn")}</button>
            </div>
          </form>
        </div>
      )}

      {/* Individual invoice modal */}
      {showIndividual && (
        <div className="dialog-overlay" onClick={() => setShowIndividual(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={submitIndividual} data-testid="individual-dues-form" style={{ maxWidth: 620 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("dues.individualTitle")}</h2>
              <button type="button" onClick={() => setShowIndividual(false)}><X size={20} /></button>
            </div>
            <p style={{ color: "#34322d", marginBottom: "1rem", fontSize: "0.88rem" }}>
              {t("dues.individualHelp")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-lodge">{t("dues.member")} *</label>
                <select
                  className="input-lodge"
                  value={indForm.member_id}
                  onChange={(e) => setIndForm({ ...indForm, member_id: e.target.value })}
                  required
                  data-testid="individual-member-select"
                >
                  <option value="">{t("dues.pickMember")}</option>
                  {members.filter((m) => m.status === "Active").map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} {m.grade ? `· ${m.grade}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-lodge">{t("dues.category")} *</label>
                <select
                  className="input-lodge"
                  value={indForm.category}
                  onChange={(e) => {
                    const newCat = e.target.value;
                    const isOneShot = newCat === "initiation_fee" || newCat === "grade_promotion";
                    setIndForm((prev) => ({
                      ...prev,
                      category: newCat,
                      grade: "",
                      period_type: isOneShot ? "one_time" : (prev.period_type === "one_time" ? "monthly" : prev.period_type),
                      period_label: isOneShot
                        ? new Date().toISOString().slice(0, 10)
                        : (prev.period_type === "one_time" ? currentPeriodLabel("monthly") : prev.period_label),
                      period_start_date: "",
                    }));
                  }}
                  data-testid="individual-category-select"
                >
                  <option value="regular">{t("dues.catRegular")}</option>
                  <option value="initiation_fee">{t("dues.catInitiation")}</option>
                  <option value="grade_promotion">{t("dues.catGrade")}</option>
                </select>
              </div>

              {indForm.category === "grade_promotion" && (
                <div data-testid="individual-grade-wrap">
                  <label className="label-lodge">{t("dues.gradeLabel")} *</label>
                  <select
                    className="input-lodge"
                    value={indForm.grade}
                    onChange={(e) => setIndForm({ ...indForm, grade: e.target.value })}
                    required
                    data-testid="individual-grade-select"
                  >
                    <option value="">{t("dues.pickGrade")}</option>
                    {Array.from({ length: 33 }, (_, i) => i + 1).map((g) => (
                      <option key={g} value={g}>{t("dues.grade")} {g}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="label-lodge">{t("dues.amount")} *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-lodge"
                  required
                  value={indForm.amount}
                  onChange={(e) => setIndForm({ ...indForm, amount: e.target.value })}
                  data-testid="individual-amount-input"
                />
              </div>

              <div>
                <label className="label-lodge">{t("dues.typeCotiz")}</label>
                <select
                  className="input-lodge"
                  value={indForm.period_type}
                  onChange={(e) => setIndForm({ ...indForm, period_type: e.target.value, period_label: e.target.value === "one_time" ? new Date().toISOString().slice(0,10) : currentPeriodLabel(e.target.value), period_start_date: "" })}
                  data-testid="individual-period-type"
                >
                  {indForm.category === "regular" ? (
                    <>
                      <option value="monthly">{t("dues.typeMonthly")}</option>
                      <option value="annual">{t("dues.typeAnnual")}</option>
                    </>
                  ) : (
                    <option value="one_time">{t("dues.typeOneTime")}</option>
                  )}
                </select>
              </div>

              {indForm.period_type !== "one_time" && (
              <div>
                <label className="label-lodge">{indForm.period_type === "monthly" ? t("dues.month") : t("dues.year")}</label>
                {indForm.period_type === "monthly" ? (
                  <input type="month" className="input-lodge" value={indForm.period_label} onChange={(e) => setIndForm({ ...indForm, period_label: e.target.value })} />
                ) : (
                  <input type="number" min="2000" max="2100" className="input-lodge" value={indForm.period_label} onChange={(e) => setIndForm({ ...indForm, period_label: e.target.value })} />
                )}
              </div>
              )}

              {indForm.period_type === "annual" && (
                <div className="md:col-span-2">
                  <label className="label-lodge">{t("dues.startDateLabel")}</label>
                  <input
                    type="date"
                    className="input-lodge"
                    value={indForm.period_start_date}
                    onChange={(e) => setIndForm({ ...indForm, period_start_date: e.target.value })}
                    data-testid="individual-start-date"
                  />
                  <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 4 }}>{t("dues.startDateHelp")}</p>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="label-lodge">{t("dues.notesOpt")}</label>
                <textarea
                  className="input-lodge"
                  rows={2}
                  value={indForm.notes}
                  onChange={(e) => setIndForm({ ...indForm, notes: e.target.value })}
                  placeholder={t("dues.notesPlaceholder")}
                />
              </div>

              <label className="md:col-span-2 flex items-center gap-2" style={{ fontSize: "0.88rem", color: "#34322d" }}>
                <input
                  type="checkbox"
                  checked={!!indForm.notify_mobile}
                  onChange={(e) => setIndForm({ ...indForm, notify_mobile: e.target.checked })}
                  data-testid="individual-notify-mobile"
                />
                <Send size={13} /> {t("dues.notifyMobile")}
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowIndividual(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={indBusy} data-testid="confirm-individual-btn">
                {indBusy ? "..." : t("dues.individualSubmit")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mark paid modal */}
      {payFor && (
        <div className="dialog-overlay" onClick={() => setPayFor(null)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={submitPay} style={{ maxWidth: 480 }} data-testid="mark-paid-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("dues.markPaidTitle")}</h2>
              <button type="button" onClick={() => setPayFor(null)}><X size={20} /></button>
            </div>
            <div className="panel mb-4" style={{ background: "var(--cream-50)" }}>
              <div className="panel-body">
                <div className="kv-row"><span className="kv-label">{t("dues.member")}</span><span className="kv-value">{payFor.member_name}</span></div>
                <div className="kv-row"><span className="kv-label">{t("dues.period")}</span><span className="kv-value">{periodHuman(payFor.period_label, payFor.period_type)}</span></div>
                <div className="kv-row"><span className="kv-label">{t("dues.amount")}</span><span className="kv-value" style={{ fontFamily: "Cinzel, serif" }}>{formatMoney(payFor.amount, payFor.currency)}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-lodge">{t("dues.paidDate")}</label>
                <LodgeDatePicker value={payForm.paid_at} onChange={(v) => setPayForm({ ...payForm, paid_at: v })} required placeholder={t("dues.selectPaidDate")} testId="input-paid-at" />
              </div>
              <div>
                <label className="label-lodge">{t("dues.method")}</label>
                <select className="input-lodge" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                  {METHODS_KEY.map((m) => <option key={m} value={m}>{t(METHOD_TKEY[m])}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("dues.notesOpt")}</label>
                <input className="input-lodge" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder={t("dues.notesPlaceholder")} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setPayFor(null)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold flex items-center gap-2" data-testid="confirm-paid-btn">
                <CheckCircle2 size={14} /> {t("dues.confirmPay")}
              </button>
            </div>
          </form>
        </div>
      )}
      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-content gold-card" style={{ maxWidth: 460, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {t("dues.edit_invoice")}
              </h2>
              <button onClick={() => setEditing(null)} className="text-gray-500"><X size={18} /></button>
            </div>
            <form onSubmit={submitEdit} className="space-y-3" data-testid="edit-dues-form">
              <div className="kv-label" style={{ fontSize: "0.7rem" }}>
                {editing.member_name}
              </div>
              <div>
                <label className="label-lodge">{t("dues.amount")}</label>
                <input type="number" step="0.01" className="input-lodge"
                  value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  data-testid="edit-dues-amount" required />
              </div>
              <div>
                <label className="label-lodge">{t("dues.period_yyyy_mm_or_yyyy")}</label>
                <input type="text" className="input-lodge"
                  value={editForm.period_label} onChange={(e) => setEditForm({ ...editForm, period_label: e.target.value })}
                  data-testid="edit-dues-period" required />
              </div>
              <div>
                <label className="label-lodge">{t("dues.dueDate")}</label>
                <input type="date" className="input-lodge"
                  value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  data-testid="edit-dues-due-date" />
              </div>
              <div>
                <label className="label-lodge">{t("dues.notes")}</label>
                <textarea className="input-lodge" rows={2}
                  value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setEditing(null)}>
                  {t("dues.cancel")}
                </button>
                <button type="submit" className="btn-primary-gold" data-testid="submit-edit-dues">
                  {t("dues.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unified EMIT NOTICE modal (Iter21) */}
      {showEmit && emitForm && (
        <div className="dialog-overlay" onClick={() => setShowEmit(false)}>
          <form
            className="dialog-panel"
            style={{ maxWidth: 720 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitEmit}
            data-testid="emit-notice-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("dues.emitNotice", "EMITE NOTIFICARE")}
              </h2>
              <button type="button" onClick={() => setShowEmit(false)}><X size={20} /></button>
            </div>

            {/* Audience toggle */}
            <div className="mb-3">
              <label className="label-lodge">
                {t("dues.recipients")}
              </label>
              <div className="flex gap-2 flex-wrap" data-testid="emit-audience-toggle">
                <button
                  type="button"
                  className={emitForm.audience === "single" ? "btn-primary-gold" : "btn-outline-gold"}
                  onClick={() => setEmitForm({ ...emitForm, audience: "single", member_ids: [] })}
                  data-testid="emit-aud-single"
                >
                  {tp("dues.single_brother")}
                </button>
                <button
                  type="button"
                  className={emitForm.audience === "multi" ? "btn-primary-gold" : "btn-outline-gold"}
                  onClick={() => setEmitForm({ ...emitForm, audience: "multi", range_mode: false })}
                  data-testid="emit-aud-multi"
                >
                  {t("dues.multi_select")}
                </button>
                <button
                  type="button"
                  className={emitForm.audience === "all" ? "btn-primary-gold" : "btn-outline-gold"}
                  onClick={() => setEmitForm({ ...emitForm, audience: "all", member_ids: [], range_mode: false })}
                  data-testid="emit-aud-all"
                >
                  {tp("dues.all_active_brothers")}
                </button>
              </div>
            </div>

            {/* Member picker */}
            {emitForm.audience === "single" && (
              <div className="mb-3">
                <label className="label-lodge">
                  {tp("dues.brother")}
                </label>
                <select
                  className="input-lodge"
                  value={emitForm.member_ids[0] || ""}
                  onChange={(e) => setEmitForm({ ...emitForm, member_ids: e.target.value ? [e.target.value] : [] })}
                  required
                  data-testid="emit-single-member"
                >
                  <option value="">{tp("dues.pick_a_brother")}</option>
                  {members.filter((m) => m.status === "Active" && m.is_deleted !== true).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} {m.grade ? `· ${m.grade}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {emitForm.audience === "multi" && (
              <div className="mb-3">
                <label className="label-lodge" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>
                    {tp("dues.brothers_selected", { count: emitForm.member_ids.length })}
                  </span>
                  <button
                    type="button"
                    className="btn-outline-gold"
                    style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem" }}
                    onClick={() => {
                      const all = members.filter((m) => m.status === "Active" && m.is_deleted !== true).map((m) => m.id);
                      setEmitForm({
                        ...emitForm,
                        member_ids: emitForm.member_ids.length === all.length ? [] : all,
                      });
                    }}
                  >
                    {emitForm.member_ids.length > 0
                      ? t("dues.clear")
                      : t("dues.select_all")}
                  </button>
                </label>
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: "auto",
                    border: "1px solid #d6c79a",
                    borderRadius: 6,
                    padding: "0.5rem",
                    background: "#fbf6e8",
                  }}
                  data-testid="emit-multi-list"
                >
                  {members.filter((m) => m.status === "Active" && m.is_deleted !== true).map((m) => {
                    const selected = emitForm.member_ids.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "0.25rem 0.4rem",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          background: selected ? "#fdf4e0" : "transparent",
                          borderRadius: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...emitForm.member_ids, m.id]
                              : emitForm.member_ids.filter((x) => x !== m.id);
                            setEmitForm({ ...emitForm, member_ids: next });
                          }}
                          data-testid={`emit-multi-${m.id}`}
                        />
                        <span style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a" }}>
                          {m.first_name} {m.last_name}
                        </span>
                        {m.grade && <span className="badge-grade" style={{ fontSize: "0.7rem" }}>{m.grade}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {emitForm.audience === "all" && (
              <p style={{ background: "#fdf4e0", border: "1px solid #c9a84c", borderRadius: 6, padding: "0.6rem 0.8rem", fontSize: "0.85rem", color: "#34322d" }} data-testid="emit-all-banner">
                <UserPlus size={13} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
                {t("dues.emit_all_banner", { count: members.filter((m) => m.status === "Active" && m.is_deleted !== true).length })}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="label-lodge">
                  {t("dues.category_2")}
                </label>
                <select
                  className="input-lodge"
                  value={emitForm.category}
                  onChange={(e) => setEmitForm({ ...emitForm, category: e.target.value, range_mode: false })}
                  data-testid="emit-category-select"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>{t(`dues.type_${c.key}`, i18n.language !== "ro" ? c.labelEn : c.labelRo)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-lodge">
                  {t("dues.amount_2")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-lodge"
                  required={!emitForm.is_exempt}
                  disabled={!!emitForm.is_exempt}
                  value={emitForm.is_exempt ? "0" : emitForm.amount}
                  onChange={(e) => setEmitForm({ ...emitForm, amount: e.target.value })}
                  placeholder={settings ? `${settings.monthly_amount} ${settings.currency}` : ""}
                  data-testid="emit-amount-input"
                />
                {emitForm.is_exempt && (
                  <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 4, color: "#9a7530" }}>
                    {t("dues.exempt_invoices_are_saved_with")}
                  </p>
                )}
              </div>

              <div>
                <label className="label-lodge">{t("dues.currency")}</label>
                <select
                  className="input-lodge"
                  value={emitForm.currency}
                  onChange={(e) => setEmitForm({ ...emitForm, currency: e.target.value })}
                  data-testid="emit-currency-select"
                >
                  {SORTED_CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>

              {emitForm.category === "cotizatie" && (
                <div>
                  <label className="label-lodge">
                    {t("dues.period_type")}
                  </label>
                  <select
                    className="input-lodge"
                    value={emitForm.period_type}
                    onChange={(e) => setEmitForm({ ...emitForm, period_type: e.target.value, range_mode: false, period_label: currentPeriodLabel(e.target.value) })}
                    data-testid="emit-period-type"
                  >
                    <option value="monthly">{t("dues.typeMonthly")}</option>
                    <option value="annual">{t("dues.typeAnnual")}</option>
                  </select>
                </div>
              )}
            </div>

            {/* Period selector — only for cotizatie */}
            {emitForm.category === "cotizatie" && (
              <div className="mt-3">
                {emitForm.period_type === "monthly" && emitForm.audience === "single" && (
                  <div className="flex gap-2 mb-2" data-testid="emit-range-toggle">
                    <button
                      type="button"
                      className={!emitForm.range_mode ? "btn-primary-gold" : "btn-outline-gold"}
                      onClick={() => setEmitForm({ ...emitForm, range_mode: false })}
                      style={{ fontSize: "0.8rem" }}
                    >
                      {t("dues.single_period")}
                    </button>
                    <button
                      type="button"
                      className={emitForm.range_mode ? "btn-primary-gold" : "btn-outline-gold"}
                      onClick={() => setEmitForm({ ...emitForm, range_mode: true })}
                      style={{ fontSize: "0.8rem" }}
                    >
                      {t("dues.range_from_to")}
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {emitForm.range_mode && emitForm.audience === "single" ? (
                    <>
                      <div>
                        <label className="label-lodge">{t("dues.from_month")}</label>
                        <input
                          type="month"
                          className="input-lodge"
                          required
                          value={emitForm.start_month}
                          onChange={(e) => setEmitForm({ ...emitForm, start_month: e.target.value })}
                          data-testid="emit-range-start"
                        />
                      </div>
                      <div>
                        <label className="label-lodge">{t("dues.to_month")}</label>
                        <input
                          type="month"
                          className="input-lodge"
                          required
                          value={emitForm.end_month}
                          onChange={(e) => setEmitForm({ ...emitForm, end_month: e.target.value })}
                          data-testid="emit-range-end"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="md:col-span-2">
                      <label className="label-lodge">
                        {emitForm.period_type === "monthly"
                          ? t("dues.month")
                          : t("dues.year")}
                      </label>
                      {emitForm.period_type === "monthly" ? (
                        <input
                          type="month"
                          className="input-lodge"
                          required
                          value={emitForm.period_label}
                          onChange={(e) => setEmitForm({ ...emitForm, period_label: e.target.value })}
                          data-testid="emit-period-month"
                        />
                      ) : (
                        <input
                          type="number"
                          min="2000"
                          max="2100"
                          className="input-lodge"
                          required
                          value={emitForm.period_label}
                          onChange={(e) => setEmitForm({ ...emitForm, period_label: e.target.value })}
                          data-testid="emit-period-year"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mark paid + Exempt toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <label className="flex items-center gap-2" style={{ fontSize: "0.88rem", color: "#34322d", cursor: emitForm.is_exempt ? "not-allowed" : "pointer", opacity: emitForm.is_exempt ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  disabled={emitForm.is_exempt}
                  checked={!!emitForm.mark_paid}
                  onChange={(e) => setEmitForm({ ...emitForm, mark_paid: e.target.checked })}
                  data-testid="emit-mark-paid"
                />
                <CheckCircle2 size={14} color="#3e6a3c" />
                {t("dues.already_paid_mark_as_paid")}
              </label>

              <label className="flex items-center gap-2" style={{ fontSize: "0.88rem", color: "#34322d", cursor: emitForm.mark_paid ? "not-allowed" : "pointer", opacity: emitForm.mark_paid ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  disabled={emitForm.mark_paid}
                  checked={!!emitForm.is_exempt}
                  onChange={(e) => setEmitForm({ ...emitForm, is_exempt: e.target.checked })}
                  data-testid="emit-is-exempt"
                />
                <AlertCircle size={14} color="#9a7530" />
                {t("dues.exempt_waive_payment")}
              </label>
            </div>

            {/* Mark-paid sub-fields */}
            {emitForm.mark_paid && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3" style={{ background: "#f1f9f0", border: "1px solid #b6dfb4", borderRadius: 6, padding: "0.8rem" }} data-testid="emit-paid-block">
                <div>
                  <label className="label-lodge">
                    {t("dues.paid_amount_optional")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-lodge"
                    value={emitForm.paid_amount}
                    onChange={(e) => setEmitForm({ ...emitForm, paid_amount: e.target.value })}
                    placeholder={emitForm.amount || ""}
                    data-testid="emit-paid-amount"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("dues.payment_method")}</label>
                  <select
                    className="input-lodge"
                    value={emitForm.payment_method}
                    onChange={(e) => setEmitForm({ ...emitForm, payment_method: e.target.value })}
                    data-testid="emit-payment-method"
                  >
                    {METHODS_KEY.map((m) => (
                      <option key={m} value={m}>{t(METHOD_TKEY[m])}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Exempt sub-fields */}
            {emitForm.is_exempt && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3" style={{ background: "#fdf4e0", border: "1px solid #c9a84c", borderRadius: 6, padding: "0.8rem" }} data-testid="emit-exempt-block">
                <div className="md:col-span-2">
                  <label className="label-lodge">
                    {t("dues.exemption_reason")}
                  </label>
                  <textarea
                    className="input-lodge"
                    rows={2}
                    required
                    value={emitForm.exempt_reason}
                    onChange={(e) => setEmitForm({ ...emitForm, exempt_reason: e.target.value })}
                    placeholder={t("dues.e_g_financial_hardship_illness")}
                    data-testid="emit-exempt-reason"
                  />
                </div>
                <div>
                  <label className="label-lodge">
                    {t("dues.exempt_until_optional")}
                  </label>
                  <input
                    type="date"
                    className="input-lodge"
                    value={emitForm.exempt_until}
                    onChange={(e) => setEmitForm({ ...emitForm, exempt_until: e.target.value })}
                    data-testid="emit-exempt-until"
                  />
                  <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 4 }}>
                    {t("dues.leave_empty_for_permanent_exemption")}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-3">
              <label className="label-lodge">{t("dues.notesOpt")}</label>
              <textarea
                className="input-lodge"
                rows={2}
                value={emitForm.notes}
                onChange={(e) => setEmitForm({ ...emitForm, notes: e.target.value })}
                data-testid="emit-notes"
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowEmit(false)}>
                {t("dues.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold flex items-center gap-2" data-testid="emit-submit-btn">
                <Send size={14} /> {t("dues.emit")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Treasury REPORT modal (Quarterly / Annual) */}
      {showReport && (
        <div className="dialog-overlay" onClick={() => setShowReport(false)}>
          <form
            className="dialog-panel"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); downloadReport(); }}
            data-testid="dues-report-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("dues.treasury_report")}
              </h2>
              <button type="button" onClick={() => setShowReport(false)}><X size={20} /></button>
            </div>
            <p style={{ fontSize: "0.86rem", color: "#34322d", marginBottom: "0.8rem" }}>
              {t("dues.generate_a_pdf_summary_grouped")}
            </p>
            <div className="flex gap-2 mb-3" data-testid="report-period-toggle">
              <button
                type="button"
                className={reportForm.period_type === "annual" ? "btn-primary-gold" : "btn-outline-gold"}
                onClick={() => setReportForm({ ...reportForm, period_type: "annual" })}
                data-testid="report-annual-btn"
              >
                {t("dues.typeAnnual")}
              </button>
              <button
                type="button"
                className={reportForm.period_type === "quarterly" ? "btn-primary-gold" : "btn-outline-gold"}
                onClick={() => setReportForm({ ...reportForm, period_type: "quarterly" })}
                data-testid="report-quarterly-btn"
              >
                {t("dues.quarterly")}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-lodge">{t("dues.year")}</label>
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  className="input-lodge"
                  value={reportForm.year}
                  onChange={(e) => setReportForm({ ...reportForm, year: e.target.value })}
                  data-testid="report-year-input"
                  required
                />
              </div>
              {reportForm.period_type === "quarterly" && (
                <div>
                  <label className="label-lodge">{t("dues.quarter")}</label>
                  <select
                    className="input-lodge"
                    value={reportForm.quarter}
                    onChange={(e) => setReportForm({ ...reportForm, quarter: e.target.value })}
                    data-testid="report-quarter-select"
                  >
                    <option value="Q1">{t("dues.q1_jan_mar")}</option>
                    <option value="Q2">{t("dues.q2_apr_jun")}</option>
                    <option value="Q3">{t("dues.q3_jul_sep")}</option>
                    <option value="Q4">{t("dues.q4_oct_dec")}</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowReport(false)}>
                {t("dues.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold flex items-center gap-2" data-testid="report-download-btn">
                <FileDown size={14} /> {t("dues.download_pdf")}
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
    </div>
  );
}
